import React, { useEffect, useState } from 'react';
import { Bot, Save } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../utils/api';
import AiUsageDetail, { textTokens } from '../components/AiUsageDetail';

// Largos máximos (todo lo que se escribe acá lo lee la IA en cada respuesta)
const LIMITS = { business_info: 3000, tone: 300, handoff_rules: 800 };

function CharCounter({ value, max }) {
  const len = (value || '').length;
  const pct = len / max;
  const color = pct >= 1 ? 'var(--danger)' : pct >= 0.85 ? '#d97706' : 'var(--text-muted)';
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 12, marginTop: 4, color }}>
      <span>≈ {textTokens(len).toLocaleString('es-AR')} tokens por respuesta</span>
      <span style={{ fontWeight: 600 }}>{len.toLocaleString('es-AR')} / {max.toLocaleString('es-AR')}</span>
    </div>
  );
}

export default function BotConfig() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/bot/settings')
      .then(res => setCfg({
        enabled: !!res.data.enabled,
        business_info: res.data.business_info || '',
        tone: res.data.tone || 'cordial y cercano',
        handoff_rules: res.data.handoff_rules || '',
      }))
      .catch(() => toast.error('No se pudo cargar la configuración'));
  }, []);

  const save = async () => {
    setSaving(true);
    try { await api.put('/bot/settings', cfg); toast.success('Configuración guardada'); }
    catch { toast.error('Error al guardar'); }
    finally { setSaving(false); }
  };

  if (!cfg) return <div style={{ color: 'var(--text-muted)', padding: 40 }}>Cargando...</div>;

  const set = (patch) => setCfg({ ...cfg, ...patch });
  const label = { fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 6, display: 'block' };
  const help = { fontSize: 12, color: 'var(--text-muted)', marginTop: 4, lineHeight: 1.4 };

  const card = { marginBottom: 0 };

  return (
    <div style={{ maxWidth: 1200 }}>
      <style>{`.bot-grid{display:grid;grid-template-columns:1fr 1fr;gap:16px;align-items:start}.bot-col{display:flex;flex-direction:column;gap:16px;min-width:0}@media(max-width:900px){.bot-grid{grid-template-columns:1fr}}`}</style>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
        <Bot size={24} color="var(--accent)" />
        <h1 style={{ fontSize: 26, fontWeight: 800 }}>Agente IA</h1>
      </div>
      <p style={{ color: 'var(--text-muted)', marginBottom: 24, fontSize: 14 }}>
        Configurá el asistente que responde automáticamente los mensajes (WhatsApp / Instagram) y deriva a una persona cuando hace falta.
      </p>

      <div className="bot-grid">
        <div className="bot-col">
          {/* On/off */}
          <div className="card" style={{ ...card, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <div>
              <div style={{ fontWeight: 600 }}>Bot activado</div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{cfg.enabled ? 'El bot responde automáticamente los mensajes entrantes.' : 'El bot está apagado — los mensajes entran pero nadie responde automático.'}</div>
            </div>
            <label style={{ cursor: 'pointer' }}>
              <input type="checkbox" checked={cfg.enabled} onChange={e => set({ enabled: e.target.checked })} style={{ width: 'auto', transform: 'scale(1.4)' }} />
            </label>
          </div>

          {/* Info del negocio */}
          <div className="card" style={card}>
            <label style={label}>Información del negocio</label>
            <textarea value={cfg.business_info} maxLength={LIMITS.business_info} onChange={e => set({ business_info: e.target.value })} rows={14}
              placeholder={'Contale al bot todo lo que necesita saber para responder:\n\n- Qué vendés / qué servicios ofrecés\n- Horarios de atención\n- Ubicación y zonas de cobertura / envíos\n- Preguntas frecuentes y sus respuestas\n- Qué NO puede responder (ej: precios) → que derive a un asesor'}
              style={{ fontFamily: 'inherit', fontSize: 13, resize: 'vertical' }} />
            <div style={help}>El bot responde <strong>solo con esta información</strong>. Si le preguntan algo que no está acá, no lo inventa: avisa que un asesor va a contactar. Cuanto más completa, mejor responde.</div>
            <CharCounter value={cfg.business_info} max={LIMITS.business_info} />
            <div style={{ ...help, marginTop: 6 }}>💡 Todo este texto lo lee la IA <b>en cada respuesta</b>: cuanto más largo, más consumo. Escribí lo esencial, en frases cortas y sin repetir información que ya esté en otro campo.</div>
          </div>
        </div>

        <div className="bot-col">
          {/* Tono */}
          <div className="card" style={card}>
            <label style={label}>Tono / estilo</label>
            <textarea value={cfg.tone} maxLength={LIMITS.tone} onChange={e => set({ tone: e.target.value })} rows={4} placeholder="Ej: cordial y cercano, con algún emoji" style={{ fontFamily: 'inherit', fontSize: 13, resize: 'vertical' }} />
            <CharCounter value={cfg.tone} max={LIMITS.tone} />
            <div style={help}>Cómo querés que hable el bot. Ej: "formal y profesional", "cercano y con emojis", "breve y directo".</div>
          </div>

          {/* Reglas de derivación */}
          <div className="card" style={card}>
            <label style={label}>¿Cuándo derivar a una persona?</label>
            <textarea value={cfg.handoff_rules} maxLength={LIMITS.handoff_rules} onChange={e => set({ handoff_rules: e.target.value })} rows={8}
              placeholder={'Ej: cuando pidan un presupuesto o precio concreto, quieran agendar un turno, o pidan hablar con un asesor.'}
              style={{ fontFamily: 'inherit', fontSize: 13, resize: 'vertical' }} />
            <CharCounter value={cfg.handoff_rules} max={LIMITS.handoff_rules} />
            <div style={help}>Escribí en tus palabras cuándo el bot tiene que dejar de responder y pasarle la conversación a un vendedor. Igual, si el cliente pide expresamente hablar con una persona, siempre deriva.</div>
          </div>

          {/* Modelo (fijo) */}
          <div className="card" style={card}>
            <label style={label}>Motor de IA</label>
            <div style={{ fontSize: 14, color: 'var(--text-primary)' }}>Claude Haiku 4.5</div>
            <div style={help}>Motor rápido y optimizado para atención por chat.</div>
          </div>

          <div><button className="btn btn-primary" onClick={save} disabled={saving}><Save size={16} /> {saving ? 'Guardando...' : 'Guardar configuración'}</button></div>
        </div>
      </div>

      {/* Consumo (abajo de todo) */}
      <div style={{ marginTop: 16 }}>
        <AiUsageDetail infoChars={cfg.business_info.length} />
      </div>
    </div>
  );
}
